 import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useNavigate } from "react-router-dom";
import { Popover, Whisper, IconButton } from 'rsuite';
import MoreIcon from '@rsuite/icons/More';
import CustomHeader from "./Utility/CustomHeader";
import ActionCellInvoice from "./Utility/ActionCellInvoice";
import { useLazyGetinvoicelistQuery, useLazyGetInvoiceQuery } from "../State/Api/InvoiceApiSlice";
import ClinicBillingTable from "./ClinicBillingTable";
import MonthlyTransactionChart from "../Components/ClinicComponent/MonthlyTransactionChart";
import TransactionViewChart from "../Components/ClinicComponent/TransactionViewChart";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import { FiSearch, FiUsers, FiX } from "react-icons/fi";
import { MdOutlineTimerOff } from "react-icons/md";
import { IoTimeOutline } from "react-icons/io5";
import { Tooltip } from "react-bootstrap";
import BillingViewFilters from "../BillingFilterJSON";
import DefaultPatientFilters from "./ClinicComponent/Filters/DefaultPatientFilters";

ModuleRegistry.registerModules([AllCommunityModule]);

function ClinicBilling() {
  const [filter, setFilter] = useState("All Invoices");
  const clinicUserId = localStorage.getItem("userClinicID");
  const [fetchInvoice, { data = [], isFetching, isSuccess, isLoading }] =
    useLazyGetinvoicelistQuery();

     const [patientDetail, setPatientDetail] = useState("");
      const [selectedFilters, setSelectedFilters] = useState([]);
      const [hasData, setHasData] = useState(true);
      const [patients, setPatients] = useState([]);
    
      const handleSearch = () => {
        let searchValue = patientDetail.trim();
    
        if (!searchValue) {
          // Clear filters if search empty
          setSelectedFilters((prev) => {
            const { fullName, patientIDUniqueID, ...rest } = prev;
            return rest;
          });
          return;
        }
    
        let newFilter;
    
        if (/^PLCRXPAT\d+$/i.test(searchValue)) {
          // Already formatted Patient ID
          newFilter = {
            patientIDUniqueID: {
              values: new Set([searchValue]),
              operator: "Contains",
            },
          };
        } else if (/^\d+$/.test(searchValue)) {
          // Only numbers → prepend prefix
          const prefixedValue = `PLCRXPAT${searchValue}`;
          newFilter = {
            patientIDUniqueID: {
              values: new Set([prefixedValue]),
              operator: "Contains",
            },
          };
        } else {
          // Otherwise treat as name
          newFilter = {
            fullName: {
              values: new Set([searchValue]),
              operator: "Contains",
            },
          };
        }
    
        setSelectedFilters((prev) => {
          const { fullName, patientIDUniqueID, ...rest } = prev;
          return { ...rest, ...newFilter };
        });
      };
    
    const handleClearSearch = () => {
      setPatientDetail("");
      setSelectedFilters((prev) => {
        const { fullName, patientIDUniqueID, ...rest } = prev;
        return rest;
      });
      setHasData(true);
    };

  const [
    triggerGetInvoice,
    { data: invoiceDropdownData, isLoading: isDropdownLoading },
  ] = useLazyGetInvoiceQuery();

  // Card interaction states - Only two cards now
  const [selectedPeriods, setSelectedPeriods] = useState({
    transaction: "month",
    patients: "month",
    invoices: "month"
  });

  // Mock data for different time periods - Only two cards
  const mockCardData = {
    transaction: {
      today: { value: 8500, change: 1.78, isPositive: true },
      month: { value: 45250, change: 365, isPositive: true },
      year: { value: 855800, change: 12365, isPositive: true },
    },
    patients: {
      today: { value: 28, change: 5, isPositive: true },
      month: { value: 1240, change: 85, isPositive: true },
      year: { value: 10200, change: 850, isPositive: true },
    },
    invoices: {
      today: { value: 28, change: 5, isPositive: true },
      month: { value: 1240, change: 85, isPositive: true },
      year: { value: 10200, change: 850, isPositive: true },
    },
  };

  // Dynamic chart data based on periods
  const chartDataSets = {
    transaction: {
      today: [15, 18, 22, 25, 20, 28, 32, 30, 35, 38],
      month: [20, 25, 30, 35, 45, 40, 50, 55, 45, 60],
      year: [30, 35, 42, 48, 55, 50, 65, 70, 60, 75]
    },
    patients: {
      today: [8, 12, 10, 15, 18, 14, 20, 22, 19, 25],
      month: [15, 20, 18, 25, 22, 28, 30, 35, 32, 40],
      year: [25, 30, 28, 35, 32, 40, 45, 50, 48, 55]
    },
    invoices: {
      today: [8, 12, 10, 15, 18, 14, 20, 22, 19, 25],
      month: [15, 20, 18, 25, 22, 28, 30, 35, 32, 40],
      year: [25, 30, 28, 35, 32, 40, 45, 50, 48, 55]
    }
  };

  // Only two card configurations
  const cardConfigs = [
    {
      key: "transaction",
      title: "Total Invoices",
      subtitle: "than last week",
      icon: <RiMoneyDollarCircleLine size={18}/>,
      gradientFrom: "#10b981",
      gradientTo: "#34d399",
      currency: true,
    },
    {
      key: "patients",
      title: "Total Patients",
      subtitle: "patients this period",
      icon: <FiUsers size={18}/>,
      gradientFrom: "#ef4444",
      gradientTo: "#f87171",
      currency: false,
    },
    {
      key: "invoices",
      title: "Pending Invoices",
      subtitle: "patients this period",
      icon: <IoTimeOutline size={18}/>,
      gradientFrom: "#efc144ff",
      gradientTo: "#eff871ff",
      currency: false,
    },
  ];

  const handleCreateInvoiceClick = async () => {
    try {
      const res = await triggerGetInvoice(clinicUserId).unwrap();
      navigate("/CreateInvoice", {
        state: { invoiceDropdownData: res },
      });
    } catch (err) {
      console.error("Failed to fetch invoice dropdown:", err);
    }
  };

  useEffect(() => {
    if (clinicUserId) {
      fetchInvoice(clinicUserId);
    }
  }, [clinicUserId, fetchInvoice]);

  const [rowData, setRowData] = useState([]);
  useEffect(() => {
    setRowData(data);
  }, [data]);

  const navigate = useNavigate();

  const handlePeriodChange = (cardKey, period) => {
    setSelectedPeriods((prev) => ({
      ...prev,
      [cardKey]: period,
    }));
  };

  const formatValue = (value, hasCurrency, cardKey = "") => {
    if (cardKey === "patients") {
      return value.toLocaleString();
    }
    if (hasCurrency) {
      return `₹${value.toLocaleString()}`;
    }
    return value.toLocaleString();
  };

  const formatChange = (change, isPositive) => {
    const sign = isPositive ? "+" : "";
    return `${sign}${change.toLocaleString()}`;
  };

  const tabButtonStyle = (key) =>
    `inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
      filter === key
        ? "bg-white text-black shadow-sm"
        : "text-muted-foreground hover:text-black"
    }`;

  const dashboardStyles = `
     .dashboard-container {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 0rem;
  }
  
  .attractive-metric-card {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    border-color: #d1d5db;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    height: 200px;
    position: relative;
    transition: all 0.3s ease;
    overflow: hidden;
  }
  

  
  .card-gradient-bg {
    position: absolute !important;
    top: -20px !important;
    right: -20px !important;
    width: 120px !important;
    height: 120px !important;
    border-radius: 50% !important;
    opacity: 1 !important;
    z-index: 1 !important;
  }
  
  .card-header-attractive {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    position: relative;
    z-index: 3;
  }
  
  .card-title-attractive {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }
  
  .card-icon-attractive {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: white;
    position: relative;
    z-index: 4;
  }
  
  .card-title-text-attractive {
    font-size: 14px;
    color: #64748b;
    font-weight: 600;
    margin: 0;
  }
  
  .period-popover-trigger {
    background: rgba(248, 250, 252, 0.9);
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 11px;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 4px;
    backdrop-filter: blur(4px);
  }
  
    .period-popover-trigger:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }
    
    
    
    .period-option-clean {
      display: block;
      width: 100%;
      padding: 8px 12px;
      background: none;
      border: none;
      text-align: left;
      cursor: pointer;
      font-size: 12px;
      border-radius: 6px;
      transition: all 0.2s;
      color: #374151;
      margin-bottom: 2px;
    }
    
    .period-option-clean:hover {
      background-color: #f3f4f6;
    }
    
    .period-option-clean.active {
      background-color: #3b82f6;
      color: white;
    }
    
    .card-value-attractive {
      font-size: 24px;
      font-weight: 700;
      color: #1e293b;
      margin: 0.5rem 0;
      line-height: 1.2;
      position: relative;
      z-index: 2;
    }
    
    .card-change-attractive {
      font-size: 13px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      position: relative;
      z-index: 2;
    }
    
    .change-positive {
      color: #16a34a;
    }
    
    .change-negative {
      color: #dc2626;
    }
    
    .mini-chart {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 60px;
      opacity: 0.8;
    }
    
    .chart-line {
      position: absolute;
      bottom: 10px;
      left: 1.5rem;
      right: 1.5rem;
      height: 40px;
    }
    
    .chart-svg {
      width: 100%;
      height: 100%;
      transition: all 0.5s ease;
    }
    
    // .chart-container {
    //   background: white;
    //   border-radius: 12px;
    //   padding: 1.5rem;
    //   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    //   border: 1px solid #e2e8f0;
    //   height: calc(50vh - 100px);
    //   margin-bottom: 1.5rem;
    // }
    .chart-container:last-child {
      margin-bottom: 0;
    }
    .invoice-tabs {
      background: #f1f5f9;
      border-radius: 8px;
      padding: 0.25rem;
      display: inline-flex;
      margin-bottom: 1rem;
    }
.table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  height: calc(100vh - 250px);
  min-height: 180px;            /* optional: prevents very small height */
  overflow-x: auto;
  overflow-y: auto;
  position: relative;
  width: 100%;
}
    .left-section {
      padding-right: 1rem;
    }
    .right-section {
      padding-left: 1rem;
    }
    .metric-cards-row {
      margin-bottom: 0rem;
    }
  `;

  const getStableColor = (key) => {
    const bgColors = [
      "bg-danger bg-opacity-50",
      "bg-success bg-opacity-50",
      "bg-primary bg-opacity-50",
      "bg-warning bg-opacity-50",
      "bg-secondary bg-opacity-50",
      "bg-info bg-opacity-50",
    ];
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % bgColors.length;
    return bgColors[index];
  };

  // RSuite Popover component for period selection
  const PeriodPopover = ({ cardKey, config }) => {
    const currentPeriod = selectedPeriods[cardKey];
    
    const popoverContent = (
      <div className="period-popover-content">
        {["today", "month", "year"].map((period) => (
          <button
            key={period}
            className={`period-option-clean ${currentPeriod === period ? "active" : ""}`}
            onClick={() => handlePeriodChange(cardKey, period)}
          >
            {period.charAt(0).toUpperCase() + period.slice(1)}
          </button>
        ))}
      </div>
    );

    return (
      <Whisper
        trigger="click"
        placement="bottomEnd"
        speaker={<Popover>{popoverContent}</Popover>}
      >
        <div className="period-popover-trigger">
          {currentPeriod.charAt(0).toUpperCase() + currentPeriod.slice(1)}
          <span style={{ fontSize: '10px' }}>▼</span>
        </div>
      </Whisper>
    );
  };

  // Render attractive metric cards with dynamic mini charts
  const renderMetricCards = () => {
    return cardConfigs.map((config) => {
      const currentPeriod = selectedPeriods[config.key];
      const currentData = mockCardData[config.key]?.[currentPeriod];
      
      if (!currentData) {
        console.error(`No data found for card: ${config.key}, period: ${currentPeriod}`);
        return null;
      }

      // Get dynamic chart data based on selected period
      const chartPoints = chartDataSets[config.key][currentPeriod];
      const maxPoint = Math.max(...chartPoints);
      const pathData = chartPoints.map((point, index) => {
        const x = (index / (chartPoints.length - 1)) * 100;
        const y = 100 - (point / maxPoint) * 80;
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
      }).join(' ');

      return (
        <div key={config.key} className="col-md-4 mb-3">
          <div className="attractive-metric-card">
            {/* Gradient Background Circle */}
            <div 
              className="card-gradient-bg" 
              style={{
                background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`
              }}
            ></div>

            <div className="card-header-attractive">
              <div className="card-title-attractive">
                <div 
                  className="card-icon-attractive" 
                  style={{ 
                    background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`
                  }}
                >
                  {config.icon}
                </div>
                <h6 className="card-title-text-attractive">{config.title}</h6>
              </div>
              
              {/* RSuite Popover for period selection */}
              <PeriodPopover cardKey={config.key} config={config} />
            </div>

            <div className="card-value-attractive">
              {formatValue(currentData.value, config.currency, config.key)}
            </div>

            <div className={`card-change-attractive ${currentData.isPositive ? 'change-positive' : 'change-negative'}`}>
              <span style={{ 
                background: currentData.isPositive ? '#dcfce7' : '#fef2f2',
                color: currentData.isPositive ? '#16a34a' : '#dc2626',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                {currentData.isPositive ? '↗' : '↘'} {currentData.change}%
              </span>
              <span className="text-muted" style={{ fontSize: '12px' }}>
                {config.subtitle}
              </span>
            </div>

            {/* Dynamic Mini Chart */}
            <div className="chart-line">
              <svg 
                className="chart-svg" 
                viewBox="0 0 100 100" 
                preserveAspectRatio="none"
                key={`${config.key}-${currentPeriod}`} // Key for re-render animation
              >
                <defs>
                  <linearGradient id={`gradient-${config.key}-${currentPeriod}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: config.gradientFrom, stopOpacity: 0.3 }} />
                    <stop offset="100%" style={{ stopColor: config.gradientFrom, stopOpacity: 0.05 }} />
                  </linearGradient>
                </defs>
                <path
                  d={`${pathData} L 100 100 L 0 100 Z`}
                  fill={`url(#gradient-${config.key}-${currentPeriod})`}
                />
                <path
                  d={pathData}
                  fill="none"
                  stroke={config.gradientFrom}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </div>
      );
    });
  };

  const colDefs = [
    {
      headerName: "Name",
      field: "patientName",
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        const name = params.value || "U";
        const initial = name.charAt(0).toUpperCase();
        const bgColor = getStableColor(name);
        return (
          <div className="d-flex align-items-center gap-2">
            <div
              className={`d-flex justify-content-center align-items-center rounded-circle text-white fw-bold ${bgColor}`}
              style={{ width: "2rem", height: "2rem", fontSize: "0.875rem" }}
            >
              {initial}
            </div>
            <span className="fw-bold text-secondary small">{name}</span>
          </div>
        );
      },
    },
    {
      headerName: "Mail",
      field: "email",
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: (params) => (
        <span className="text-secondary fw-medium small">
          {params.value ||
            `${params.data.patientName
              ?.toLowerCase()
              .replace(" ", "")}@email.com`}
        </span>
      ),
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        let bgColor = "bg-light";
        let textColor = "text-secondary";
        if (params.value === "Paid") {
          bgColor = "bg-success";
          textColor = "text-white";
        } else if (params.value === "Partially Paid") {
          bgColor = "bg-warning";
          textColor = "text-white";
        } else if (params.value === "Unpaid") {
          bgColor = "bg-danger";
          textColor = "text-white";
        }
        return (
          <span className={`badge ${bgColor} ${textColor} px-2 py-1`}>
            {params.value}
          </span>
        );
      },
    },
    {
      headerName: "Value",
      field: "amount",
      minWidth: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params) => (
        <span className="fw-bold">
          ₹{(params.value || Math.floor(Math.random() * 50000) + 5000).toLocaleString()}
        </span>
      ),
    },
  ];

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      filter: true,
      sortable: true,
      resizable: true,
      headerComponent: CustomHeader,
    }),
    []
  );

  const filteredInvoices = rowData.filter((inv) =>
    filter === "All Invoices" ? true : inv.status === filter
  );

  const tabs = ["All Invoices", "Unpaid", "Paid", "Partially Paid"];

  return (
    <>
      <style>{dashboardStyles}</style>
      <div className="dashboard-container">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12 d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h1 className="h2 fw-bold mb-0" style={{fontSize:'24px'}}>Payment</h1>
            </div>

             <div className="d-flex align-items-center justify-content-end gap-3">
              <div className="d-flex align-items-center gap-3">
                {/* <label className="relative">
                                <input
                                  type="text"
                                  className="pr-20 pl-4 py-2 border border-[#7eb592] rounded-lg w-full text-sm   focus:outline-none ring-2 ring-[#7eb592]/20 focus:outline focus:border-[#7eb592] focus:ring-2 focus:ring-[#7eb592]/20"
                                  placeholder="Enter patient name or ID"
                                  value={patientDetail}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setPatientDetail(value);
                
                                    if (value.trim() === "") {
                                      setSelectedFilters((prev) => {
                                        const { fullName, patientIDUniqueID, ...rest } = prev;
                                        return rest;
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                />
                                {patientDetail && (
                                  <span
                                    className="absolute right-12 top-9 transform -translate-y-1/2 p-2 rounded-md text-gray-500 hover:text-red-400 cursor-pointer"
                                    onClick={handleClearSearch}
                                  >
                                    <FiX size={16} />
                                  </span>
                                )}
                                <span
                                  className="absolute right-[0.3rem] top-9 transform -translate-y-1/2 bg-[#7eb592] hover:bg-gray-200 p-2 rounded-md text-white cursor-pointer"
                                  onClick={handleSearch}
                                >
                                  <FiSearch size={16} />
                                </span>
                              </label>
                
                              
                
                              <Whisper
                                placement="top"
                                trigger="hover"
                                speaker={<Tooltip>Filter Options</Tooltip>}
                              >
                                <DefaultPatientFilters
                                  defaultFilters={BillingViewFilters.default}
                                  additionalFilters={BillingViewFilters.additional}
                                  selectedFilters={selectedFilters}
                                  setSelectedFilters={setSelectedFilters}
                                  setHasData={setHasData}
                                />
                              </Whisper> */}
                <button
                  className="gradient-button"
                  style={{ color: "#fff" }}
                  onClick={handleCreateInvoiceClick}
                >
                  + Create invoice
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Row */}
        <div className="row">
          {/* Left Section - Cards and Table (col-8) */}
          <div className="col-lg-12 left-section">
            {/* Attractive Metric Cards - 1x2 Grid */}
            <div className="row metric-cards-row">{renderMetricCards()}</div>

            {/* Table Section */}
            {/* <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">All Invoice</h5>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm">
                  🔍 Search
                </button>
                <button className="btn btn-outline-secondary btn-sm">
                  Filter ⚙
                </button>
              </div>
            </div> */}

            {/* Table */}
            <div className="table-container mb-2">
              <ClinicBillingTable
                data={filteredInvoices} 
                isFetching={isFetching}
                isLoading={isLoading}
                isSuccess={isSuccess}
                colDefs={colDefs}
                defaultColDef={defaultColDef}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ClinicBilling;
